/**
 * AutoUpdateManager.js - Autonomous Zero-Button Mobile & Desktop Auto-Updater
 * Standardized Recipe for Antigravity Workspace WebApps & Games
 * 
 * Features:
 * 1. Startup (1.5s), mobile resume (visibilitychange / focus throttled to 15s), and periodic (3m) polling
 * 2. Autonomous, zero-button update execution:
 *    - Immediate seamless automatic reload on safe states
 *    - Deferred auto-reload on active modal / media interaction
 * 3. 5-Layer cache-busting pipeline with PRISTINE URL navigation (strictly ZERO query strings in address bar):
 *    - Origin server no-cache revalidation headers
 *    - Pre-flight HTTP cache invalidation fetch (`cache: 'reload'`)
 *    - Full Cache Storage API eviction (`caches.delete()`)
 *    - Service Worker unregistration
 *    - Transient sync token stripped synchronously in <head> before first paint
 *    - Anti-thrashing circuit breaker with 60s backoff
 */

export class AutoUpdateManager {
  constructor(app = null, currentVersion = null, options = {}) {
    this.app = app;
    this.currentVersion = this.resolveCurrentVersion(currentVersion);
    this.latestVersion = this.currentVersion;
    this.isUpdateAvailable = false;
    this.pendingAutoUpdate = false;
    this.checkIntervalMs = options.checkIntervalMs || (3 * 60 * 1000); // 3 minutes
    this.resumeThrottleMs = options.resumeThrottleMs || 15000; // 15 seconds
    this.endpoint = options.endpoint || '/version.json';
    this.onUpdateDetected = options.onUpdateDetected || null;
    this.safeStatePredicate = options.isSafeToAutoReload || null;

    this.intervalId = null;
    this.deferredIntervalId = null;
    this.lastCheckTime = 0;

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      this.init();
    }
  }

  resolveCurrentVersion(paramVersion) {
    let metaVer = null;
    let badgeVer = null;
    if (typeof document !== 'undefined') {
      const meta = document.querySelector('meta[name="app-version"]');
      if (meta && meta.content && meta.content.trim()) {
        metaVer = meta.content.trim().replace(/^v/, '');
      }
      const badge = document.getElementById('corner-version-badge') || document.querySelector('.corner-version-badge');
      if (badge && badge.textContent && badge.textContent.trim()) {
        badgeVer = badge.textContent.trim().replace(/^v/, '');
      }
    }
    const buildVer = (typeof __APP_VERSION__ !== 'undefined' && __APP_VERSION__) ? String(__APP_VERSION__).trim().replace(/^v/, '') : null;
    const cleanParam = (paramVersion && typeof paramVersion === 'string' && paramVersion.trim() && paramVersion !== '1.0.0')
      ? paramVersion.trim().replace(/^v/, '')
      : null;

    const candidates = [metaVer, badgeVer, buildVer, cleanParam].filter(Boolean);
    if (!candidates.length) return '0.0.0';

    let highest = candidates[0];
    for (let i = 1; i < candidates.length; i++) {
      if (this.isNewerVersion(candidates[i], highest)) {
        highest = candidates[i];
      }
    }
    return highest;
  }

  init() {
    // 0. Proactively sanitize address bar of any legacy query strings
    this.sanitizeUrl();

    // Suppress autonomous background reload during automated testing environments (Puppeteer / Playwright)
    if (typeof navigator !== 'undefined' && navigator.webdriver) {
      return;
    }

    // 1. Initial check after page startup stabilization
    const initTimer = setTimeout(() => this.checkForUpdate(), 1500);
    if (initTimer && typeof initTimer.unref === 'function') {
      initTimer.unref();
    }

    // 2. Check whenever user resumes or switches back to the tab/app
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          const elapsed = Date.now() - this.lastCheckTime;
          if (elapsed > this.resumeThrottleMs) {
            this.checkForUpdate();
          }
        }
      });
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('focus', () => {
        const elapsed = Date.now() - this.lastCheckTime;
        if (elapsed > this.resumeThrottleMs) {
          this.checkForUpdate();
        }
      });
    }

    // 3. Periodic background check
    this.intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    }, this.checkIntervalMs);
    if (this.intervalId && typeof this.intervalId.unref === 'function') {
      this.intervalId.unref();
    }

    // 4. Autonomous deferred update poller (checks every 1.5s if pending update is awaiting safe state)
    this.deferredIntervalId = setInterval(() => {
      if (this.pendingAutoUpdate) {
        this.checkDeferredUpdate();
      }
    }, 1500);
    if (this.deferredIntervalId && typeof this.deferredIntervalId.unref === 'function') {
      this.deferredIntervalId.unref();
    }
  }

  sanitizeUrl() {
    if (typeof window === 'undefined' || !window.location) return;
    try {
      const search = window.location.search;
      if (!search) return;
      if (!search.includes('__webapp_sync') && !search.includes('__usk_sync') && !search.includes('__sync')) {
        return; // No sync tokens present; preserve benign query params (e.g. ?ride=...) intact
      }
      if (window.history && typeof window.history.replaceState === 'function') {
        const params = new URLSearchParams(search);
        params.delete('__webapp_sync');
        params.delete('__usk_sync');
        params.delete('__sync');
        const remaining = params.toString();
        const cleanPath = (window.location.pathname || '/') + (remaining ? '?' + remaining : '') + (window.location.hash || '');
        window.history.replaceState(null, '', cleanPath);
      }
    } catch (_) {}
  }

  async checkForUpdate() {
    if (typeof fetch === 'undefined') return;
    this.lastCheckTime = Date.now();

    try {
      const sep = this.endpoint.includes('?') ? '&' : '?';
      const res = await fetch(`${this.endpoint}${sep}_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!res.ok) return;
      const data = await res.json();
      const serverVersion = data && data.version ? String(data.version).trim() : null;

      if (serverVersion && (this.isNewerVersion(serverVersion, this.currentVersion) || serverVersion !== this.currentVersion)) {
        this.latestVersion = serverVersion;
        this.isUpdateAvailable = true;

        if (typeof this.onUpdateDetected === 'function') {
          try {
            this.onUpdateDetected(serverVersion, this.currentVersion);
          } catch (_) {}
        }

        // Anti-Thrashing Circuit Breaker: Prevent rapid reload loops if server CDN is lagging
        let priorAttemptTime = 0;
        const attemptKey = `webapp_reload_attempt_${serverVersion}`;
        try {
          if (typeof window !== 'undefined' && window.sessionStorage && typeof window.sessionStorage.getItem === 'function') {
            priorAttemptTime = parseInt(window.sessionStorage.getItem(attemptKey) || '0', 10);
          }
        } catch (_) {}

        const now = Date.now();
        if (priorAttemptTime > 0 && (now - priorAttemptTime) < 60000) {
          console.warn(`[AutoUpdateManager] Reload recently attempted for v${serverVersion}. Backing off for 60s.`);
          return;
        }

        this.applyUpdateStrategy();
      } else if (serverVersion && serverVersion === this.currentVersion) {
        // Clean up stale reload attempt keys when successfully running latest version
        try {
          if (typeof window !== 'undefined' && window.sessionStorage && typeof window.sessionStorage.removeItem === 'function') {
            window.sessionStorage.removeItem(`webapp_reload_attempt_${serverVersion}`);
          }
        } catch (_) {}
      }
    } catch (_) {
      // Offline, airplane mode, or network failure - fail silently without user friction
    }
  }

  isNewerVersion(serverVer, currentVer) {
    if (!serverVer || !currentVer) return false;
    if (serverVer === currentVer) return false;

    const sParts = serverVer.split('.').map(Number);
    const cParts = currentVer.split('.').map(Number);

    for (let i = 0; i < Math.max(sParts.length, cParts.length); i++) {
      const s = sParts[i] || 0;
      const c = cParts[i] || 0;
      if (s > c) return true;
      if (s < c) return false;
    }
    return false;
  }

  isSafeToAutoReload() {
    if (typeof this.safeStatePredicate === 'function') {
      try {
        return Boolean(this.safeStatePredicate(this.app));
      } catch (_) {
        return true;
      }
    }

    // Default safe-state checks: not inside open modal
    if (typeof document !== 'undefined') {
      const activeModal = document.querySelector('.lightbox-modal.active, .modal.active');
      if (activeModal) return false;
    }

    return true;
  }

  applyUpdateStrategy() {
    if (this.isSafeToAutoReload()) {
      this.pendingAutoUpdate = false;
      return this.performFrictionlessReload().catch(() => {});
    } else {
      // Defer update until safe disengagement (modal close)
      this.pendingAutoUpdate = true;
      return Promise.resolve();
    }
  }

  checkDeferredUpdate() {
    if (this.pendingAutoUpdate && this.isSafeToAutoReload()) {
      this.pendingAutoUpdate = false;
      return this.performFrictionlessReload().catch(() => {});
    }
    return Promise.resolve();
  }

  async performFrictionlessReload() {
    if (typeof window === 'undefined') return;

    // 0. Auto-save current state
    try {
      if (typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('app:save-state'));
      }
    } catch (_) {}

    // Record reload attempt timestamp in sessionStorage
    const attemptKey = `webapp_reload_attempt_${this.latestVersion}`;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage && typeof window.sessionStorage.setItem === 'function') {
        window.sessionStorage.setItem(attemptKey, Date.now().toString());
      }
    } catch (_) {}

    // 1. Evict full Cache Storage API
    try {
      if ('caches' in window && typeof caches.keys === 'function') {
        const keys = await caches.keys();
        if (Array.isArray(keys)) {
          await Promise.all(keys.map(k => caches.delete(k)));
        }
      }
    } catch (_) {}

    // 2. Unregister all active Service Workers
    try {
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && typeof navigator.serviceWorker.getRegistrations === 'function') {
        const regs = await navigator.serviceWorker.getRegistrations();
        if (Array.isArray(regs)) {
          await Promise.all(regs.map(r => r && typeof r.unregister === 'function' ? r.unregister() : Promise.resolve()));
        }
      }
    } catch (_) {}

    // 3. Clear transient storage while preserving circuit breaker backoff
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        if (typeof window.sessionStorage.removeItem === 'function') {
          const keys = Object.keys(window.sessionStorage).filter(k => !k.startsWith('webapp_reload_attempt_'));
          keys.forEach(k => window.sessionStorage.removeItem(k));
        } else if (typeof window.sessionStorage.clear === 'function') {
          window.sessionStorage.clear();
        }
      }
      if (typeof window !== 'undefined' && window.localStorage) {
        const lsKeys = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          if (k && (k.startsWith('btcnjcc_media_rides_') || k === 'btcnjcc_media_rides_v2')) {
            lsKeys.push(k);
          }
        }
        lsKeys.forEach(k => {
          try { window.localStorage.removeItem(k); } catch (_) {}
        });
      }
    } catch (_) {}

    // 4. Pre-flight revalidation fetch to prime browser HTTP disk cache
    try {
      if (typeof fetch !== 'undefined' && window.location && window.location.pathname) {
        await fetch(window.location.pathname, {
          cache: 'reload',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
      }
    } catch (_) {}

    // 5. Hard clean URL navigation
    try {
      let origin = window.location.origin;
      if (!origin && window.location.href) {
        try { origin = new URL(window.location.href).origin; } catch (_) {}
      }
      if (!origin && window.location.protocol && window.location.host) {
        origin = window.location.protocol + '//' + window.location.host;
      }
      const pathname = window.location.pathname || '/';
      const cleanUrl = (origin || '') + pathname;

      // Transient navigation token forces browser document cache bypass
      const syncParam = `__webapp_sync=${encodeURIComponent(this.latestVersion)}_${Date.now()}`;
      const navUrl = cleanUrl.includes('?') ? `${cleanUrl}&${syncParam}` : `${cleanUrl}?${syncParam}`;

      if (typeof window.location.replace === 'function') {
        window.location.replace(navUrl);
      } else {
        window.location.href = navUrl;
      }
    } catch (_) {
      if (typeof window.location.reload === 'function') {
        window.location.reload();
      }
    }
  }

  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.deferredIntervalId) {
      clearInterval(this.deferredIntervalId);
      this.deferredIntervalId = null;
    }
    this.pendingAutoUpdate = false;
  }
}
